import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Typography, Stack, ButtonBase, Collapse, IconButton, Tooltip,
  LinearProgress,
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { tableFromIPC } from "apache-arrow";
import { segmentOptionsFor } from "./utils/segmentConfig";
import { schemaRequestFields } from "./utils/schemaTypes";
import { DEMO_LABELS } from "./utils/demographicLabels";
import ProviderLogo, { PROVIDER_COLORS } from "./ProviderLogo";

const AGE_BUCKETS = [
  { code: 1, label: "18–34", phrase: "18–34s" },
  { code: 2, label: "35–64", phrase: "35–64s" },
  { code: 3, label: "65+", phrase: "over-65s" },
];

function NetBar({ value, maxAbs, color, height = 14 }) {
  const pct = maxAbs > 0 ? Math.abs(value) / maxAbs * 50 : 0;
  const isPos = value >= 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
      <Box sx={{ flex: 1, height, position: "relative", bgcolor: "#f0f2f7", borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ position: "absolute", top: 0, bottom: 0, left: "50%", width: "1px", bgcolor: "#ccc", zIndex: 1 }} />
        <Box sx={{
          position: "absolute", top: 1, bottom: 1,
          ...(isPos
            ? { left: "50%", width: `${pct}%`, borderRadius: "0 4px 4px 0" }
            : { right: "50%", width: `${pct}%`, borderRadius: "4px 0 0 4px" }),
          bgcolor: color || (isPos ? "#22c55e" : "#ef4444"),
          transition: "width 0.4s ease",
        }} />
      </Box>
      <Typography sx={{
        fontSize: 12, fontWeight: 600, minWidth: 40, textAlign: "right",
        fontFamily: "'DM Sans', sans-serif",
        color: isPos ? "#16a34a" : "#dc2626",
      }}>
        {isPos ? "+" : ""}{value.toFixed(1)}
      </Typography>
    </Box>
  );
}

function BreakdownBar({ segments, height = 10 }) {
  const total = segments.reduce((s, seg) => s + Math.max(0, seg.value), 0);
  if (total === 0) return null;
  return (
    <Box sx={{ display: "flex", borderRadius: 1, overflow: "hidden", height, width: "100%" }}>
      {segments.map((seg, i) => {
        const pct = (seg.value / total) * 100;
        if (pct < 0.5) return null;
        return (
          <Tooltip key={i} title={`${seg.label}: ${seg.value.toFixed(1)}%`} arrow placement="top">
            <Box sx={{
              width: `${pct}%`, bgcolor: seg.color, height: "100%",
              transition: "width 0.4s ease",
              "&:first-of-type": { borderRadius: "4px 0 0 4px" },
              "&:last-of-type": { borderRadius: "0 4px 4px 0" },
            }} />
          </Tooltip>
        );
      })}
    </Box>
  );
}

function DemoPills({ filters }) {
  const entries = Object.entries(filters || {});
  if (!entries.length) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, mt: -0.75, flexWrap: "wrap", gap: 0.25 }}>
      {entries.map(([k, v]) => (
        <Typography key={k} sx={{
          fontSize: 10, color: "#7c3aed", fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
          bgcolor: "#f5f3ff", px: 0.75, py: 0.15, borderRadius: 0.5,
        }}>
          {DEMO_LABELS[k]?.[v] || `${k}: ${v}`}
        </Typography>
      ))}
    </Stack>
  );
}

function HeadToHead({ a, b, group, onClose, isCategorical, demoFilters }) {
  const polarity = group?.polarity;
  const posKeys = [].concat(polarity?.pos || []);
  const negKeys = [].concat(polarity?.neg || []);
  const labels = group?.labels || {};
  const colors = group?.colors || {};

  const breakdownFor = (provider) => {
    if (!provider?.data) return [];
    return (group?.keys || []).filter(k => !k.includes(":dk:")).map(k => ({
      label: labels[k] || k,
      value: provider.data[k] ?? 0,
      color: posKeys.includes(k) ? "#34d399" : negKeys.includes(k) ? "#f9a8a8" : "#cbd5e1",
    }));
  };

  const netFor = (p) => {
    if (!p?.data) return 0;
    const pos = posKeys.reduce((s, k) => s + (p.data[k] ?? 0), 0);
    const neg = negKeys.reduce((s, k) => s + (p.data[k] ?? 0), 0);
    return pos - neg;
  };

  if (isCategorical) {
    const sectorKeys = (group?.keys || []).filter(k => !k.includes(":dk:") && !k.includes(":other:"));
    const sectors = sectorKeys.map(k => {
      const vA = a.data?.[k] ?? 0;
      const vB = b.data?.[k] ?? 0;
      return { key: k, label: labels[k] || k, color: colors[k] || "#999", vA, vB };
    }).sort((x, y) => Math.max(y.vA, y.vB) - Math.max(x.vA, x.vB));
    const maxVal = Math.max(...sectors.map(s => Math.max(s.vA, s.vB)), 1);
    const topA = a.sectors?.[0];
    const topB = b.sectors?.[0];

    return (
      <Box sx={{ p: 2, position: "relative" }}>
        <IconButton size="small" onClick={onClose} sx={{ position: "absolute", top: 4, right: 4 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#6b7280", mb: 1.5,
          fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Head to head
        </Typography>
        <DemoPills filters={demoFilters} />
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
          <Stack alignItems="center" spacing={0.5}>
            <ProviderLogo name={a.name} size={40} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>{a.name}</Typography>
            {topA && (
              <Typography sx={{ fontSize: 9, color: "#6b7280", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                Top: {topA.label?.split(/[,(]/)[0].trim()}
              </Typography>
            )}
          </Stack>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>vs</Typography>
          <Stack alignItems="center" spacing={0.5}>
            <ProviderLogo name={b.name} size={40} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>{b.name}</Typography>
            {topB && (
              <Typography sx={{ fontSize: 9, color: "#6b7280", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
                Top: {topB.label?.split(/[,(]/)[0].trim()}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          {sectors.slice(0, 8).map(s => {
            const diff = s.vA - s.vB;
            return (
              <Box key={s.key}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: 9, color: "#6b7280", fontFamily: "'DM Sans', sans-serif" }}>
                    {s.label.split(/[,(]/)[0].trim()}
                  </Typography>
                  {Math.abs(diff) >= 2 && (
                    <Typography sx={{ fontSize: 8, fontWeight: 600, color: diff > 0 ? "#16a34a" : "#dc2626", fontFamily: "'DM Sans', sans-serif" }}>
                      {diff > 0 ? `+${diff.toFixed(0)}` : diff.toFixed(0)}pp
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography sx={{ fontSize: 8, width: 24, textAlign: "right", fontFamily: "'DM Sans', sans-serif",
                    fontWeight: s.vA >= s.vB ? 700 : 400,
                    color: s.vA > s.vB ? "#16a34a" : "#9ca3af",
                  }}>
                    {s.vA.toFixed(0)}%
                  </Typography>
                  <Box sx={{ flex: 1, height: 6, bgcolor: "#f0f2f7", borderRadius: 0.5, overflow: "hidden" }}>
                    <Box sx={{ width: `${(s.vA / maxVal) * 100}%`, height: "100%", bgcolor: s.color, opacity: s.vA >= s.vB ? 0.85 : 0.4, borderRadius: 0.5 }} />
                  </Box>
                  <Box sx={{ flex: 1, height: 6, bgcolor: "#f0f2f7", borderRadius: 0.5, overflow: "hidden" }}>
                    <Box sx={{ width: `${(s.vB / maxVal) * 100}%`, height: "100%", bgcolor: s.color, opacity: s.vB >= s.vA ? 0.85 : 0.4, borderRadius: 0.5 }} />
                  </Box>
                  <Typography sx={{ fontSize: 8, width: 24, fontFamily: "'DM Sans', sans-serif",
                    fontWeight: s.vB >= s.vA ? 700 : 400,
                    color: s.vB > s.vA ? "#16a34a" : "#9ca3af",
                  }}>
                    {s.vB.toFixed(0)}%
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
        <Stack direction="row" justifyContent="center" spacing={3} sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 9, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
            ◀ {a.name.split(" ")[0]}
          </Typography>
          <Typography sx={{ fontSize: 9, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
            {b.name.split(" ")[0]} ▶
          </Typography>
        </Stack>
      </Box>
    );
  }

  const netA = netFor(a);
  const netB = netFor(b);
  const diff = netA - netB;

  return (
    <Box sx={{
      border: "1px solid #e5e7eb", borderRadius: 2, p: 2, bgcolor: "#fafbfd",
      position: "relative",
    }}>
      <IconButton size="small" onClick={onClose}
        sx={{ position: "absolute", top: 4, right: 4 }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#6b7280", mb: 1.5,
        fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Head to head
      </Typography>
      <DemoPills filters={demoFilters} />
      <Typography sx={{ fontSize: 10, color: "#9ca3af", mt: -1, mb: 1.5,
        fontFamily: "'DM Sans', sans-serif" }}>
        How each provider&rsquo;s customers answer this question — net score per customer base.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
        <Stack alignItems="center" spacing={0.5}>
          <ProviderLogo name={a.name} size={40} />
          <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
            {a.name}
          </Typography>
          <Typography sx={{
            fontSize: 18, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
            color: netA >= 0 ? "#0d9488" : "#b45309",
            opacity: netA >= netB ? 1 : 0.5,
          }}>
            {netA >= 0 ? "+" : ""}{netA.toFixed(1)}
          </Typography>
        </Stack>

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
          vs
        </Typography>

        <Stack alignItems="center" spacing={0.5}>
          <ProviderLogo name={b.name} size={40} />
          <Typography sx={{ fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
            {b.name}
          </Typography>
          <Typography sx={{
            fontSize: 18, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
            color: netB >= 0 ? "#0d9488" : "#b45309",
            opacity: netB >= netA ? 1 : 0.5,
          }}>
            {netB >= 0 ? "+" : ""}{netB.toFixed(1)}
          </Typography>
        </Stack>
      </Stack>

      {Math.abs(diff) >= 0.5 ? (
        <Box sx={{
          textAlign: "center", mb: 1.5, py: 0.75, px: 1.5, bgcolor: "#eef2ff",
          borderRadius: 1.5, mx: "auto", width: "fit-content",
        }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
            color: "#4338ca",
          }}>
            {(diff > 0 ? a : b).name} customers are {Math.abs(diff).toFixed(1)}pp warmer
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          textAlign: "center", mb: 1.5, py: 0.75, px: 1.5, bgcolor: "#f8f9fc",
          borderRadius: 1.5, mx: "auto", width: "fit-content",
        }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
            color: "#6b7280",
          }}>
            The two customer bases feel much the same
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 2, mb: 1 }}>
        {[["#34d399", "Positive"], ["#cbd5e1", "Neutral"], ["#f9a8a8", "Negative"]].map(([color, label]) => (
          <Typography key={label} sx={{ fontSize: 9, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
            <Box component="span" sx={{
              display: "inline-block", width: 7, height: 7, borderRadius: "50%",
              bgcolor: color, mr: 0.4, verticalAlign: "middle",
            }} />
            {label}
          </Typography>
        ))}
      </Stack>

      <Stack spacing={1.5}>
        {[a, b].map((provider) => (
          <Box key={provider.name}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#6b7280", mb: 0.5,
              fontFamily: "'DM Sans', sans-serif" }}>
              {provider.name}
            </Typography>
            <BreakdownBar segments={breakdownFor(provider)} height={12} />
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
              {breakdownFor(provider).map((seg, i) => (
                <Typography key={i} sx={{ fontSize: 10, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
                  <Box component="span" sx={{
                    display: "inline-block", width: 6, height: 6, borderRadius: "50%",
                    bgcolor: seg.color, mr: 0.3, verticalAlign: "middle",
                  }} />
                  {seg.value.toFixed(0)}%
                </Typography>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function ProviderComparison({
  group,
  schema,
  waveSelect,
  segmentFilter,
  onSegmentChange,
  clientSlug,
  areaCode,
  areaLevel,
  wardCode,
  selectedKey,
  demographics,
  onDemographicChange,
  areaLabel,
}) {
  const [providerData, setProviderData] = useState({});
  const [baselineData, setBaselineData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [h2h, setH2h] = useState(null); // [providerA, providerB]
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [ageBreakdown, setAgeBreakdown] = useState(false);
  const [ageData, setAgeData] = useState({});
  const [ageLoading, setAgeLoading] = useState(false);
  const [providerAllData, setProviderAllData] = useState({});

  const isEnergySchema = schema === "energy";
  const isOctopusUser = ["octopus", "octoenergy", "stack-data-strategy"].includes(clientSlug);
  // TEMP: on the Stack tab the panel stays provider-neutral — no Octopus row
  // emphasis and no Octopus-specific insight — so demos don't advertise whose
  // work this was. Octopus's own tab keeps both.
  const brandNeutral = String(clientSlug || "").toLowerCase() === "stack-data-strategy";
  const isCategorical = group?.type === "categorical";
  const drillKey = isCategorical && selectedKey && (group?.keys || []).includes(selectedKey) ? selectedKey : null;
  const drillLabel = drillKey ? (group?.labels?.[drillKey] || drillKey).split(/[,(]/)[0].trim() : null;

  const providers = useMemo(() => {
    if (!isEnergySchema) return [];
    return segmentOptionsFor("energy").filter(([, label]) =>
      label !== "Other" && label !== "Don't know"
    );
  }, [isEnergySchema]);

  const keys = useMemo(() => group?.keys || [], [group]);
  const polarity = group?.polarity;
  const posKeys = useMemo(() => [].concat(polarity?.pos || []), [polarity]);
  const negKeys = useMemo(() => [].concat(polarity?.neg || []), [polarity]);

  const demoKey = JSON.stringify(demographics || {});
  const demoFilters = useMemo(() => {
    const f = {};
    if (demographics) {
      Object.entries(demographics).forEach(([k, v]) => {
        if (v != null && v !== "") f[k] = v;
      });
    }
    return f;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoKey]);

  const hasDemoFilters = Object.keys(demoFilters).length > 0;

  const fetchRows = useCallback(async (filters) => {
    const level = areaCode && areaLevel === "ward" ? "ward" : "constituency";
    const body = {
      areaCodes: areaCode ? [areaCode] : [],
      theme: "pol",
      groupKeys: keys,
      ...schemaRequestFields(schema || "energy"),
      filters,
    };
    const res = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return [];
    const buf = await res.arrayBuffer();
    const table = tableFromIPC(buf);
    let rows = table.toArray().map(r => r.toJSON());
    if (wardCode) {
      const ward = rows.filter(r => String(r.area_cd) === wardCode);
      if (ward.length) rows = ward;
    }
    return rows;
  }, [areaCode, areaLevel, keys, schema, wardCode]);

  const avgFromRows = useCallback((rows) => {
    const avg = {};
    keys.forEach(k => {
      const vals = rows.map(r => Number(r[k])).filter(v => Number.isFinite(v));
      avg[k] = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
    });
    return avg;
  }, [keys]);

  const fetchProviderData = useCallback(async () => {
    if (!keys.length || !providers.length || !isEnergySchema) return;
    setLoading(true);
    const results = {};

    try {
      const providerPromises = providers.map(async ([code, label]) => {
        const rows = await fetchRows({ segment: [code], ...demoFilters });
        results[label] = avgFromRows(rows);
      });

      const baselinePromise = (async () => {
        const rows = await fetchRows({ ...demoFilters });
        setBaselineData(avgFromRows(rows));
      })();

      await Promise.all([...providerPromises, baselinePromise]);
    } catch (e) {
      console.error("[ProviderComparison] fetch error:", e);
    }

    setProviderData(results);
    setLoading(false);
  // waveSelect kept as a dep so a wave change refetches, mirroring the map.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keys, providers, isEnergySchema, fetchRows, avgFromRows, demoFilters, waveSelect]);

  // Age breakdown: one request per provider × age bucket. Any active age slice
  // is superseded (the toggle clears it); other demographic slices still apply.
  const fetchAgeData = useCallback(async () => {
    if (!keys.length || !providers.length || !isEnergySchema) return;
    setAgeLoading(true);
    const results = {};
    const baseFilters = { ...demoFilters };
    delete baseFilters.age;

    try {
      await Promise.all(providers.flatMap(([code, label]) =>
        AGE_BUCKETS.map(async ({ code: bucket }) => {
          const rows = await fetchRows({ segment: [code], ...baseFilters, age: bucket });
          if (!results[label]) results[label] = {};
          results[label][bucket] = avgFromRows(rows);
        })
      ));
    } catch (e) {
      console.error("[ProviderComparison] age breakdown fetch error:", e);
    }

    setAgeData(results);
    setAgeLoading(false);
  }, [keys, providers, isEnergySchema, fetchRows, avgFromRows, demoFilters]);

  // Unfiltered per-provider baseline ("all customers of this provider"), used
  // to show how the active demographic subgroup differs from the provider's
  // whole customer base. Only fetched while a demographic filter is active.
  const fetchProviderAllData = useCallback(async () => {
    if (!keys.length || !providers.length || !isEnergySchema) return;
    const results = {};
    try {
      await Promise.all(providers.map(async ([code, label]) => {
        const rows = await fetchRows({ segment: [code] });
        results[label] = avgFromRows(rows);
      }));
    } catch (e) {
      console.error("[ProviderComparison] all-customers fetch error:", e);
    }
    setProviderAllData(results);
  }, [keys, providers, isEnergySchema, fetchRows, avgFromRows]);

  useEffect(() => {
    if (expanded && isEnergySchema) {
      fetchProviderData();
    }
  }, [expanded, fetchProviderData, isEnergySchema]);

  useEffect(() => {
    if (expanded && isEnergySchema && hasDemoFilters) {
      fetchProviderAllData();
    }
  }, [expanded, isEnergySchema, hasDemoFilters, fetchProviderAllData]);

  useEffect(() => {
    if (expanded && ageBreakdown && isEnergySchema && !isCategorical) {
      fetchAgeData();
    }
  }, [expanded, ageBreakdown, fetchAgeData, isEnergySchema, isCategorical]);

  const netOf = useCallback((data) => {
    if (!data) return 0;
    const pos = posKeys.reduce((s, k) => s + (data[k] ?? 0), 0);
    const neg = negKeys.reduce((s, k) => s + (data[k] ?? 0), 0);
    return pos - neg;
  }, [posKeys, negKeys]);

  const baselineNet = useMemo(() => netOf(baselineData), [baselineData, netOf]);

  const ageNets = useMemo(() => {
    const out = {};
    Object.entries(ageData).forEach(([name, buckets]) => {
      out[name] = {};
      Object.entries(buckets).forEach(([b, d]) => { out[name][b] = netOf(d); });
    });
    return out;
  }, [ageData, netOf]);

  // Per provider per bucket: net minus the average net of the other providers
  // in the same bucket — "how does this provider's young base compare with
  // young customers of the rest of the field".
  const ageVsField = useMemo(() => {
    const out = {};
    const names = Object.keys(ageNets);
    AGE_BUCKETS.forEach(({ code: b }) => {
      names.forEach(name => {
        const own = ageNets[name]?.[b];
        const others = names.filter(n => n !== name)
          .map(n => ageNets[n]?.[b]).filter(v => Number.isFinite(v));
        if (!others.length || !Number.isFinite(own)) return;
        const fieldAvg = others.reduce((s, v) => s + v, 0) / others.length;
        if (!out[name]) out[name] = {};
        out[name][b] = own - fieldAvg;
      });
    });
    return out;
  }, [ageNets]);

  const ageMaxAbs = useMemo(() => {
    const vals = Object.values(ageNets).flatMap(b => Object.values(b).map(v => Math.abs(v)));
    return vals.length ? Math.max(...vals, 1) : 1;
  }, [ageNets]);

  const octopusInsight = useMemo(() => {
    const oct = ageVsField["Octopus Energy"];
    if (!oct) return null;
    const entries = AGE_BUCKETS
      .filter(({ code }) => Number.isFinite(oct[code]))
      .map(({ code, phrase }) => ({ code, phrase, delta: oct[code] }));
    if (!entries.length) return null;
    const best = entries.reduce((a, c) => (c.delta > a.delta ? c : a));
    const worst = entries.reduce((a, c) => (c.delta < a.delta ? c : a));
    // "vs field" is vs the AVERAGE of the other providers — being above it does
    // not mean ranking first, so the headline states the actual rank.
    const rankIn = (bucket) => {
      const nets = Object.entries(ageNets)
        .map(([name, buckets]) => ({ name, net: buckets?.[bucket] }))
        .filter(r => Number.isFinite(r.net))
        .sort((a, b) => b.net - a.net);
      const idx = nets.findIndex(r => r.name === "Octopus Energy");
      return idx >= 0 ? { rank: idx + 1, of: nets.length } : null;
    };
    const ordinal = (n) => (n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`);
    const parts = [];
    if (best.delta > 0.5) {
      const r = rankIn(best.code);
      parts.push(r?.rank === 1
        ? `Octopus leads all providers among ${best.phrase}, ${best.delta.toFixed(1)}pp above the rest`
        : `Octopus is strongest among ${best.phrase}${r ? ` (${ordinal(r.rank)} of ${r.of})` : ""}, ${best.delta.toFixed(1)}pp above the other-provider average`);
    }
    if (worst.delta < -0.5) {
      parts.push(`${parts.length ? "weakest" : "Octopus is weakest"} among ${worst.phrase}, ${Math.abs(worst.delta).toFixed(1)}pp below average`);
    }
    if (!parts.length) return "Octopus performs close to the other-provider average across all age groups.";
    return parts.join("; ") + ".";
  }, [ageVsField, ageNets]);

  // Human-readable description of the active slice, e.g. "18–34 women".
  const sliceLabel = useMemo(() => {
    const parts = [];
    if (!ageBreakdown && demoFilters.age != null) parts.push(DEMO_LABELS.age?.[demoFilters.age]);
    if (demoFilters.gender != null) parts.push((DEMO_LABELS.gender?.[demoFilters.gender] || "").toLowerCase());
    return parts.filter(Boolean).join(" ");
  }, [demoFilters, ageBreakdown]);

  const toggleAgeBreakdown = useCallback(() => {
    const next = !ageBreakdown;
    if (next && demoFilters.age != null) onDemographicChange?.("age", null);
    setAgeBreakdown(next);
  }, [ageBreakdown, demoFilters.age, onDemographicChange]);

  const ranked = useMemo(() => {
    if (isCategorical) {
      const sectorKeys = keys.filter(k => !k.includes(":dk:") && !k.includes(":other:"));
      const baselineDrill = drillKey && baselineData && areaCode ? (baselineData[drillKey] ?? 0) : null;
      return providers.map(([code, label]) => {
        const data = providerData[label] || {};
        const sectors = sectorKeys
          .map(k => ({ key: k, value: data[k] ?? 0, label: group?.labels?.[k] || k, color: group?.colors?.[k] || "#999" }))
          .sort((a, b) => b.value - a.value);
        const drillValue = drillKey ? (data[drillKey] ?? 0) : (sectors[0]?.value ?? 0);
        const vsBaseline = baselineDrill != null ? drillValue - baselineDrill : null;
        const allData = providerAllData[label];
        const vsGroup = hasDemoFilters && drillKey && allData
          ? drillValue - (allData[drillKey] ?? 0) : null;
        return { code, name: label, topValue: drillValue, vsBaseline, vsGroup, sectors, data };
      }).sort((a, b) => b.topValue - a.topValue);
    }
    return providers.map(([code, label]) => {
      const data = providerData[label] || {};
      const pos = posKeys.reduce((s, k) => s + (data[k] ?? 0), 0);
      const neg = negKeys.reduce((s, k) => s + (data[k] ?? 0), 0);
      const net = pos - neg;
      const vsBaseline = baselineData && areaCode ? net - baselineNet : null;
      const allData = providerAllData[label];
      const vsGroup = hasDemoFilters && allData ? net - netOf(allData) : null;
      return { code, name: label, net, vsBaseline, vsGroup, data };
    }).sort((a, b) => b.net - a.net);
  }, [providers, providerData, providerAllData, hasDemoFilters, netOf, posKeys, negKeys, isCategorical, keys, group, drillKey, baselineData, baselineNet]);

  const maxAbs = useMemo(() => {
    if (isCategorical) {
      const allVals = ranked.flatMap(r => (r.sectors || []).map(s => s.value));
      return allVals.length ? Math.max(...allVals, 1) : 1;
    }
    const vals = ranked.map(r => Math.abs(r.net));
    return vals.length ? Math.max(...vals, 1) : 1;
  }, [ranked, isCategorical]);

  const isSelected = useCallback((code) => {
    return Array.isArray(segmentFilter) && segmentFilter.includes(code);
  }, [segmentFilter]);

  const handleProviderClick = useCallback((code) => {
    if (!onSegmentChange) return;
    if (isSelected(code)) {
      onSegmentChange([]);
    } else {
      onSegmentChange([code]);
    }
  }, [onSegmentChange, isSelected]);

  const handleH2hSelect = useCallback((provider) => {
    setH2h(prev => {
      if (!prev) return [provider];
      if (prev.length === 1) {
        if (prev[0].code === provider.code) return null;
        return [prev[0], provider];
      }
      return [provider];
    });
  }, []);

  // h2h stores click-time snapshots; resolve them against the current ranked
  // data so the panel follows filter/slice changes instead of going stale.
  const h2hResolved = useMemo(() => {
    if (h2h?.length !== 2) return null;
    const a = ranked.find(r => r.code === h2h[0].code);
    const b = ranked.find(r => r.code === h2h[1].code);
    return a && b ? [a, b] : null;
  }, [h2h, ranked]);

  // hideProviderCompare opts a question out (e.g. the provider question itself,
  // where splitting by provider is circular).
  if (!isEnergySchema || !isOctopusUser || group?.hideProviderCompare) return null;
  if (!["likert", "categorical"].includes(group?.type)) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <ButtonBase
        onClick={() => setExpanded(!expanded)}
        sx={{
          width: "100%", py: 1, px: 2, borderRadius: 1.5,
          border: expanded ? "1px solid #e0e7ff" : "1px solid #e5e7eb",
          bgcolor: expanded ? "#f0f4ff" : "#fafbfd",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          transition: "all 0.2s",
          "&:hover": { bgcolor: "#f0f4ff", borderColor: "#c7d2fe" },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <CompareArrowsIcon sx={{ fontSize: 16, color: "#6366f1" }} />
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{
              fontSize: 12, fontWeight: 600, color: "#374151",
              fontFamily: "'DM Sans', sans-serif",
            }}>
              Compare by energy provider
            </Typography>
            <Typography sx={{
              fontSize: 10, color: "#6b7280",
              fontFamily: "'DM Sans', sans-serif",
            }}>
              This question, filtered to each provider&rsquo;s customers
            </Typography>
          </Box>
        </Stack>
        <ExpandMoreIcon sx={{
          fontSize: 16, color: "#9ca3af",
          transform: expanded ? "rotate(180deg)" : "none",
          transition: "transform 0.2s",
        }} />
      </ButtonBase>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1, border: "1px solid #e5e7eb", borderRadius: 2, overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <LinearProgress sx={{ borderRadius: 1, bgcolor: "#f0f2f7",
                "& .MuiLinearProgress-bar": { bgcolor: "#6366f1" } }} />
              <Typography sx={{ fontSize: 11, color: "#9ca3af", mt: 1, textAlign: "center",
                fontFamily: "'DM Sans', sans-serif" }}>
                Loading provider data...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Header */}
              <Box sx={{
                px: 2, py: 1, bgcolor: "#f8f9fc", borderBottom: "1px solid #f0f2f7",
              }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{
                    fontSize: 10, fontWeight: 600, color: "#6b7280",
                    fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}>
                    {isCategorical
                      ? (drillLabel
                          ? (sliceLabel ? `${drillLabel} among ${sliceLabel} customers` : `${drillLabel} — by provider's customers`)
                          : (sliceLabel ? `Sector importance among ${sliceLabel} customers` : "Sector importance — by provider's customers"))
                      : ageBreakdown
                        ? (sliceLabel ? `Net trust by age among ${sliceLabel} customers` : "Net trust by age — by provider's customers")
                        : (sliceLabel ? `Net trust among ${sliceLabel} customers` : "Net trust — by provider's customers")}
                  </Typography>
                  <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    {!isCategorical && (
                      <ButtonBase onClick={toggleAgeBreakdown}
                        sx={{ fontSize: 10, fontWeight: 600,
                          color: ageBreakdown ? "#4338ca" : "#6366f1",
                          bgcolor: ageBreakdown ? "#eef2ff" : "transparent",
                          fontFamily: "'DM Sans', sans-serif", px: 0.75, py: 0.25,
                          borderRadius: 0.5, "&:hover": { bgcolor: "#f0f4ff" } }}>
                        {ageBreakdown ? "Hide ages" : "By age"}
                      </ButtonBase>
                    )}
                    {!(ageBreakdown && !isCategorical) && (
                      <ButtonBase onClick={() => setShowBreakdown(!showBreakdown)}
                        sx={{ fontSize: 10, color: "#6366f1", fontWeight: 600,
                          fontFamily: "'DM Sans', sans-serif", px: 0.75, py: 0.25,
                          borderRadius: 0.5, "&:hover": { bgcolor: "#f0f4ff" } }}>
                        {showBreakdown ? "Hide detail" : "Show detail"}
                      </ButtonBase>
                    )}
                  </Stack>
                </Box>
                <Typography sx={{
                  fontSize: 9, color: "#9ca3af", mt: 0.25,
                  fontFamily: "'DM Sans', sans-serif",
                }}>
                  Each row shows how this question&rsquo;s responses look among that provider&rsquo;s customers.
                  Set demographic filters below and this comparison updates to match.
                </Typography>

                {(hasDemoFilters || areaLabel) && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.25 }}>
                    {areaLabel && (
                      <Typography sx={{ fontSize: 9, color: "#6366f1", fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
                        bgcolor: "#f0f4ff", px: 0.75, py: 0.15, borderRadius: 0.5 }}>
                        {areaLabel}
                      </Typography>
                    )}
                    {Object.entries(demoFilters).map(([k, v]) => (
                      <Typography key={k} sx={{ fontSize: 9, color: "#7c3aed", fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
                        bgcolor: "#f5f3ff", px: 0.75, py: 0.15, borderRadius: 0.5 }}>
                        {DEMO_LABELS[k]?.[v] || `${k}: ${v}`}
                      </Typography>
                    ))}
                    {baselineData && areaCode && !isCategorical && (
                      <Typography sx={{ fontSize: 9, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif",
                        px: 0.75, py: 0.15 }}>
                        {sliceLabel ? `all ${sliceLabel}` : "all adults"} avg: {baselineNet >= 0 ? "+" : ""}{baselineNet.toFixed(1)}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>

              {ageBreakdown && !isCategorical && ageLoading && (
                <LinearProgress sx={{ height: 2, bgcolor: "#f0f2f7",
                  "& .MuiLinearProgress-bar": { bgcolor: "#6366f1" } }} />
              )}

              {/* Leaderboard */}
              <Stack spacing={0}>
                {ranked.map((provider, idx) => {
                  const active = isSelected(provider.code);
                  const inH2h = h2h?.some(p => p.code === provider.code);
                  const isOctopus = !brandNeutral && provider.name === "Octopus Energy";
                  return (
                    <Box key={provider.code} sx={{
                      px: 2, py: 1,
                      bgcolor: active ? "#f0f4ff" : isOctopus ? "#fdf2f8" : inH2h ? "#faf5ff" : idx % 2 ? "#fafbfd" : "#fff",
                      borderLeft: isOctopus ? "3px solid #E20074" : "3px solid transparent",
                      borderBottom: idx < ranked.length - 1 ? "1px solid #f5f5f5" : "none",
                      cursor: "pointer",
                    }}
                      onClick={() => handleProviderClick(provider.code)}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {/* Rank */}
                        <Typography sx={{
                          fontSize: 11, fontWeight: 700, color: "#9ca3af", width: 14, textAlign: "center",
                          fontFamily: "'DM Sans', sans-serif",
                        }}>
                          {idx + 1}
                        </Typography>

                        {/* Logo */}
                        <ProviderLogo name={provider.name} size={26} />

                        {/* Name + bar */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
                            <Typography sx={{
                              fontSize: 12, fontWeight: active ? 700 : 500,
                              color: active ? "#4338ca" : "#374151",
                              fontFamily: "'DM Sans', sans-serif",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {provider.name}
                            </Typography>
                          </Stack>
                          {isCategorical ? (
                            drillKey ? (
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                                <Box sx={{ flex: 1, height: 10, bgcolor: "#f0f2f7", borderRadius: 0.5, overflow: "hidden" }}>
                                  <Box sx={{
                                    width: `${(provider.topValue / maxAbs) * 100}%`, height: "100%",
                                    bgcolor: group?.colors?.[drillKey] || PROVIDER_COLORS[provider.name] || "#6366f1",
                                    borderRadius: 0.5, transition: "width 0.4s ease",
                                  }} />
                                </Box>
                                <Typography sx={{
                                  fontSize: 11, fontWeight: 600, color: "#374151",
                                  minWidth: 38, textAlign: "right", fontFamily: "'DM Sans', sans-serif",
                                }}>
                                  {provider.topValue.toFixed(1)}%
                                </Typography>
                                {provider.vsGroup != null && Math.abs(provider.vsGroup) >= 0.5 && (
                                  <Tooltip title={`vs all ${provider.name} customers (without demographic filters)`} placement="top">
                                    <Typography sx={{
                                      fontSize: 10, fontWeight: 600, flexShrink: 0,
                                      color: provider.vsGroup > 0 ? "#16a34a" : "#dc2626",
                                      fontFamily: "'DM Sans', sans-serif",
                                    }}>
                                      {provider.vsGroup > 0 ? "+" : "−"}{Math.abs(provider.vsGroup).toFixed(1)} vs all customers
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Stack>
                            ) : (
                              <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                                {(provider.sectors || []).slice(0, showBreakdown ? undefined : 3).map(s => (
                                  <Stack key={s.key} direction="row" spacing={0.5} alignItems="center">
                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
                                    <Tooltip title={s.label} placement="top">
                                      <Typography sx={{
                                        fontSize: 9, color: "#6b7280", minWidth: 70, maxWidth: 70,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        fontFamily: "'DM Sans', sans-serif",
                                      }}>
                                        {s.label.split(/[,(]/)[0].trim()}
                                      </Typography>
                                    </Tooltip>
                                    <Box sx={{ flex: 1, height: 5, bgcolor: "#f0f2f7", borderRadius: 0.5, overflow: "hidden" }}>
                                      <Box sx={{
                                        width: `${(s.value / maxAbs) * 100}%`, height: "100%",
                                        bgcolor: s.color, borderRadius: 0.5, transition: "width 0.4s ease",
                                      }} />
                                    </Box>
                                    <Typography sx={{
                                      fontSize: 9, fontWeight: 600, color: "#374151",
                                      minWidth: 34, textAlign: "right", fontFamily: "'DM Sans', sans-serif",
                                    }}>
                                      {s.value.toFixed(1)}%
                                    </Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            )
                          ) : ageBreakdown ? (
                            <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                              {AGE_BUCKETS.map(({ code: bucket, label: bLabel }) => {
                                const net = ageNets[provider.name]?.[bucket];
                                const delta = ageVsField[provider.name]?.[bucket];
                                const hasDelta = Number.isFinite(delta) && Math.abs(delta) >= 0.5;
                                return (
                                  <Stack key={bucket} direction="row" spacing={0.5} alignItems="center">
                                    <Typography sx={{
                                      fontSize: 9, color: "#6b7280", width: 34, flexShrink: 0,
                                      fontFamily: "'DM Sans', sans-serif",
                                    }}>
                                      {bLabel}
                                    </Typography>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <NetBar value={net ?? 0} maxAbs={ageMaxAbs}
                                        color={PROVIDER_COLORS[provider.name]} height={8} />
                                    </Box>
                                    <Tooltip title="vs the average of the other providers' customers in this age group" placement="top">
                                      <Typography sx={{
                                        fontSize: 10, fontWeight: 600, width: 80, flexShrink: 0, textAlign: "right",
                                        fontFamily: "'DM Sans', sans-serif",
                                        color: (delta ?? 0) > 0 ? "#16a34a" : "#dc2626",
                                      }}>
                                        {hasDelta ? `${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)} vs field` : ""}
                                      </Typography>
                                    </Tooltip>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          ) : (
                            <>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <NetBar
                                    value={provider.net}
                                    maxAbs={maxAbs}
                                    color={PROVIDER_COLORS[provider.name]}
                                  />
                                </Box>
                                {provider.vsGroup != null && Math.abs(provider.vsGroup) >= 0.5 && (
                                  <Tooltip title={`vs all ${provider.name} customers (without demographic filters)`} placement="top">
                                    <Typography sx={{
                                      fontSize: 10, fontWeight: 600, flexShrink: 0,
                                      color: provider.vsGroup > 0 ? "#16a34a" : "#dc2626",
                                      fontFamily: "'DM Sans', sans-serif",
                                    }}>
                                      {provider.vsGroup > 0 ? "+" : "−"}{Math.abs(provider.vsGroup).toFixed(1)} vs all customers
                                    </Typography>
                                  </Tooltip>
                                )}
                              </Stack>
                              {showBreakdown && provider.data && Object.keys(provider.data).length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                  <BreakdownBar
                                    height={6}
                                    segments={(group?.keys || []).filter(k => !k.includes(":dk:")).map(k => ({
                                      label: group?.labels?.[k] || k,
                                      value: provider.data[k] ?? 0,
                                      color: posKeys.includes(k) ? "#34d399" : negKeys.includes(k) ? "#f9a8a8" : "#cbd5e1",
                                    }))}
                                  />
                                </Box>
                              )}
                            </>
                          )}
                        </Box>

                        {/* H2H button */}
                        <Tooltip title="Add to head-to-head" placement="left">
                          <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); handleH2hSelect(provider); }}
                            sx={{
                              width: 24, height: 24,
                              border: inH2h ? "2px solid #7c3aed" : "1px solid #e5e7eb",
                              bgcolor: inH2h ? "#f5f3ff" : "transparent",
                              "&:hover": { bgcolor: "#f5f3ff", borderColor: "#7c3aed" },
                            }}>
                            <CompareArrowsIcon sx={{ fontSize: 12, color: inH2h ? "#7c3aed" : "#9ca3af" }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>

                      {active && (
                        <Typography sx={{
                          fontSize: 9, color: "#6366f1", fontWeight: 500, mt: 0.5, pl: 4.5,
                          fontFamily: "'DM Sans', sans-serif",
                        }}>
                          Map filtered to {provider.name} customers
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>

              {/* H2H panel */}
              <Collapse in={!!h2hResolved}>
                {h2hResolved && (
                  <Box sx={{ p: 1.5, borderTop: "1px solid #e5e7eb" }}>
                    <HeadToHead
                      a={{ ...h2hResolved[0] }}
                      b={{ ...h2hResolved[1] }}
                      group={group}
                      onClose={() => setH2h(null)}
                      isCategorical={isCategorical}
                      demoFilters={demoFilters}
                    />
                  </Box>
                )}
              </Collapse>

              {/* Auto-generated insight for the age breakdown */}
              {ageBreakdown && !isCategorical && !ageLoading && octopusInsight && !brandNeutral && (
                <Box sx={{ px: 2, py: 1, bgcolor: "#fdf2f8", borderTop: "1px solid #f0f2f7" }}>
                  <Typography sx={{
                    fontSize: 10, color: "#9d174d", fontWeight: 500,
                    fontFamily: "'DM Sans', sans-serif", textAlign: "center",
                  }}>
                    {octopusInsight}
                  </Typography>
                </Box>
              )}

              {/* Hint */}
              <Box sx={{ px: 2, py: 1, bgcolor: "#f8f9fc", borderTop: "1px solid #f0f2f7" }}>
                <Typography sx={{
                  fontSize: 10, color: "#9ca3af", fontFamily: "'DM Sans', sans-serif",
                  textAlign: "center",
                }}>
                  Click a provider to filter the map to its customers &middot; Use <CompareArrowsIcon sx={{ fontSize: 10, verticalAlign: "middle" }} /> to compare two
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
